import { zodResolver } from "@hookform/resolvers/zod";
import { Box, MenuItem, TextField } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import FormAutocomplete from "../../General/FormAutocomplete";

import { useTranslation } from "react-i18next";
import { useLocationData } from "../../../hooks";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { AddressForm as AddressFormType, getAddressFormSchema } from "../../../types/forms/entity";
import { EntityAddress } from "../../../types/model/entity/EntityAddress";

interface AddressFormProps {
  addressData?: EntityAddress;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: AddressFormType) => Promise<void>;
}

export default function AddressForm({
  addressData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<AddressFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const [selectedDistrito, setSelectedDistrito] = useState<number | null>();
  const [selectedConcelho, setSelectedConcelho] = useState<number | null>();

  const validationSchema = getAddressFormSchema(t);
  const methods = useForm<AddressFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      address: addressData?.address || "",
      locality: addressData?.locality || "",
      zipCode: addressData?.zipCode || "",
      zipArea: addressData?.zipArea || "",
      freguesiaId: addressData?.freguesiaId ?? null,
      streetType: addressData?.streetType ?? null,
      doorNumber: addressData?.doorNumber ?? null,
      floor: addressData?.floor ?? null,
    },
  });

  const { distritos, concelhos, freguesias } = useLocationData(selectedDistrito, selectedConcelho);

  const handleFormSubmit = async (values: AddressFormType) => {
    await onSubmit(values);
  };

  useEffect(() => {
    if (addressData) {
      setSelectedDistrito(addressData.distritoId ?? undefined);
      setSelectedConcelho(addressData.concelhoId ?? undefined);
    }
  }, [addressData]);

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t("entities.entity.addresses.street-type")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("streetType")}
              error={!!methods.formState.errors.streetType}
              helperText={methods.formState.errors.streetType?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 5 }}>
            <TextField
              fullWidth
              label={`${t("entities.entity.addresses.address")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("address")}
              error={!!methods.formState.errors.address}
              helperText={methods.formState.errors.address?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 6, sm: 2 }}>
            <TextField
              fullWidth
              label={t("entities.entity.addresses.door-number")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("doorNumber")}
              error={!!methods.formState.errors.doorNumber}
              helperText={methods.formState.errors.doorNumber?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 6, sm: 2 }}>
            <TextField
              fullWidth
              label={t("entities.entity.addresses.floor")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("floor")}
              error={!!methods.formState.errors.floor}
              helperText={methods.formState.errors.floor?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 2.5 }}>
            <TextField
              fullWidth
              label={`${t("entities.entity.addresses.zip-code")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("zipCode")}
              error={!!methods.formState.errors.zipCode}
              helperText={methods.formState.errors.zipCode?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 1.5 }}>
            <TextField
              fullWidth
              label={`${t("entities.entity.addresses.zip-area")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("zipArea")}
              error={!!methods.formState.errors.zipArea}
              helperText={methods.formState.errors.zipArea?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              label={`${t("entities.entity.addresses.locality")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("locality")}
              error={!!methods.formState.errors.locality}
              helperText={methods.formState.errors.locality?.message}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label={t("entities.entity.addresses.distrito")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || distritos.length === 0 || isSaving || isDeleting}
              value={
                distritos.some((d) => d.id === selectedDistrito)
                  ? selectedDistrito
                  : ""
              }
              onChange={(e) =>
                setSelectedDistrito(e.target.value ? Number(e.target.value) : null)
              }
              sx={{ ...formStyles }}
            >
              <MenuItem value="">—</MenuItem>
              {distritos.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label={t("entities.entity.addresses.concelho")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={
                !isEditing || !selectedDistrito || concelhos.length === 0 || isSaving || isDeleting
              }
              value={
                concelhos.some((c) => c.id === selectedConcelho)
                  ? selectedConcelho
                  : ""
              }
              onChange={(e) =>
                setSelectedConcelho(e.target.value ? Number(e.target.value) : null)
              }
              sx={{ ...formStyles }}
            >
              <MenuItem value="">—</MenuItem>
              {concelhos.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <FormAutocomplete
              name="freguesiaId"
              label={t("entities.entity.addresses.freguesia")}
              options={freguesias}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={!selectedConcelho}
              formStyles={formStyles}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}